export enum InterfaceType {
  PAC = "PAC",
  KIDS = "KIDS",
}
export interface PlaceHoldInfoImpl {
  message: string;
  guardiansBlocking: string[];
  success: boolean;
  /**
   * barcode if item level hold,
   * recordNumber if title level hold
   * */
  bibliographicId: string;
  pickupBranchId: string;
  titleLevelHold: boolean;
  interfaceType: InterfaceType;
  downloadable: boolean;
  serialItem: boolean;
  pacHold: boolean;
  kidsHold: boolean;
}

export interface PlaceHoldPayloadItem
  extends Pick<
    PlaceHoldInfoImpl,
    | "pickupBranchId"
    | "bibliographicId"
    | "titleLevelHold"
    | "downloadable"
    | "serialItem"
    | "interfaceType"
  > {}

export interface PlaceHoldReturnImpl {
  hostSystemDiag: HostSystemDiagImpl;
  placeHoldInfos: PlaceHoldInfoImpl[];
}
export interface CancelHoldInfoImpl {
  desireNumber: number;
  whichBranch: number | null;
  bibliographicId: string;
  holdQueueLength: number;
  success: boolean;
  downloadable: boolean;
  cancelable: boolean;
  status: string;
  messageToken: string;
}
export interface CancelHoldReturnImpl {
  hostSystemDiag: HostSystemDiagImpl;
  cancelHoldInfos: CancelHoldInfoImpl[];
}
export interface SuspendHoldInfo {
  desireNumber: number;
  success: boolean;
  suspendDate: Date | null;
  queuePosition: number;
  bibliographicId: string;
  pickupBranchId: string;
  downloadable: boolean;
  suspendable: boolean;
}
export interface SuspendHoldReturn {
  hostSystemDiag: HostSystemDiagImpl;
  suspendHoldInfos: SuspendHoldInfo[];
}
import axios from "axios";
import { HostSystemItemSortType } from "../../dto/HostSystemItemSortType";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { HostSystemDiagImpl } from "../../dto/HostSystemDiagImpl";
import { HostSystemDiag } from "../pages/resource/ItemAvailability";

export const placeHoldRequest = async (
  placeHoldInfos: PlaceHoldPayloadItem[],
  limitTitleLevelHoldsToPatronBranch: boolean,
): Promise<PlaceHoldReturnImpl> => {
  const response = await axios.post<PlaceHoldReturnImpl>(
    `/requests/${limitTitleLevelHoldsToPatronBranch}`,
    placeHoldInfos,
  );
  return response.data;
};

export const cancelHold = async (
  holds: HostSystemItemView[],
): Promise<CancelHoldReturnImpl> => {
  const cancelHoldInfos: Omit<
    CancelHoldInfoImpl,
    "cancelable" | "messageToken"
  >[] = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      status,
      downloadable,
    }) => {
      return {
        desireNumber: parseInt(holdNumber),
        success: false,
        holdQueueLength: parseInt(holdQueueLength),
        bibliographicId: bibliographicId,
        whichBranch: holdPickupBranchId,
        status: status,
        downloadable: downloadable,
      };
    },
  );
  const response = await axios.post<CancelHoldReturnImpl>("/requests/cancel", {
    cancelHoldInfos: JSON.stringify(cancelHoldInfos),
  });
  return response.data;
};

export const suspendHold = async (
  holds: HostSystemItemView[],
  suspendUntil: Date,
): Promise<SuspendHoldReturn> => {
  // TODO confirm discrepencies in property types for SuspendHoldInfo and HostSystemItemView
  // (parseInt and toString below)

  const suspendHoldInfos = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      downloadable,
    }) => {
      const holdInfo: Omit<SuspendHoldInfo, "suspendable"> = {
        desireNumber: parseInt(holdNumber),
        success: false,
        suspendDate: suspendUntil,
        queuePosition: parseInt(holdQueueLength),
        bibliographicId,
        pickupBranchId: holdPickupBranchId!.toString(), // TODO: create narrower interface for Holds based on HostSystemItem to avoid null errors
        downloadable: downloadable,
      };
      return holdInfo;
    },
  );
  const response = await axios.post<SuspendHoldReturn>("/requests/suspend", {
    suspendHoldInfos: JSON.stringify(suspendHoldInfos),
  });
  return response.data;
};

export const unsuspendHold = async (
  holds: HostSystemItemView[],
): Promise<SuspendHoldReturn> => {
  const suspendHoldInfos = holds.map(
    ({
      holdNumber,
      holdQueueLength,
      bibliographicId,
      holdPickupBranchId,
      downloadable,
    }) => {
      const holdInfo: Omit<SuspendHoldInfo, "suspendable"> = {
        desireNumber: parseInt(holdNumber),
        success: false,
        suspendDate: null,
        queuePosition: parseInt(holdQueueLength),
        bibliographicId,
        pickupBranchId: holdPickupBranchId!.toString(), // TODO: create narrower interface for Holds based on HostSystemItem to avoid null errors
        downloadable: downloadable,
      };
      return holdInfo;
    },
  );

  const response = await axios.post<SuspendHoldReturn>("/requests/unsuspend", {
    suspendHoldInfos: JSON.stringify(suspendHoldInfos),
  });
  return response.data;
};

export interface GetAllHoldsResponse {
  holds: HostSystemItemView[];
  hostSystemDiag: HostSystemDiag | null;
  totalArrivedHolds: number;
  totalHolds: number;
}

export interface EResourceFormat {
  formatId: string;
  formatToken: string;
  availableOn: Date;
  preRelease: boolean;
}

export const getHoldsSortable = async (
  page: number,
  count: number,
  sortType: HostSystemItemSortType,
  includeResources = true,
): Promise<GetAllHoldsResponse> => {
  const start = page * count;
  const response = await axios.get<GetAllHoldsResponse>(
    `/requests/${start}/${count}/${sortType}/${includeResources}`,
  );
  return response.data;
};

export const getChildHoldsSortable = async (
  id: string,
  page: number,
  count: number,
  sortType: HostSystemItemSortType,
  includeResources = true,
): Promise<GetAllHoldsResponse> => {
  const start = page * count;

  const response = await axios.get(
    `requests/child/${id}/${start}/${count}/${sortType}`,
  );
  return response.data;
};

export const getHoldsTotalCount = async () => {
  const response = await axios.get(`requests/count`);
  return response.data.count;
};

// TODO: need a child holds /count endpoint
// export const getChildHoldsTotalCount = async (id: string) => {
//   const response = await axios.get(`requests/child/${id}/count`);
//   return response.data;
// };
